/**
 * Catálogos do diagrama — a biblioteca de componentes e os posicionamentos padrão.
 *
 * Os dois são **globais**: valem para todos os projetos, e é por isso que moram
 * no menu do topo e não numa aba de projeto.
 *
 * **A biblioteca está suja, e a tela mostra a sujeira.** Os nomes vieram de
 * colagem e a maioria tem espaço sobrando na frente e atrás; há um registro em
 * branco. Aparar na exibição faria a tela mentir sobre o que está gravado — e é
 * o gravado que o desenho usa. Então a tela marca o espaço em vez de escondê-lo,
 * e aparar é uma edição que alguém faz de propósito.
 *
 * **Os posicionamentos quase não existem.** Dos 14 sistemas que sondei em
 * produção, só o 33 tem registro, e são dois. A tela abre pedindo o sistema
 * justamente porque a lista é por sistema — e diz que o vazio é o normal aqui,
 * para ninguém concluir que a tela quebrou.
 */

import { useMemo, useRef, useState } from "react"
import {
  usarBiblioteca, usarConferirBiblioteca, usarExcluirDaBiblioteca, usarExcluirPosicionamento,
  usarPosicionamentos, usarSalvarBiblioteca, usarSalvarPosicionamento,
  type ComponenteDaBiblioteca, type PosicionamentoPadrao,
} from "@/api/catalogosDoDiagrama"
import { usarSistemasDeConteudo } from "@/api/catalogosDeConteudo"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { Faixa } from "@/kit/Faixa"
import {
  ordenarLista, SeletorDeOrdem, usarOrdemDaLista, usarRecemIncluido, usarRolarAteRecente,
} from "@/kit/ordemDaLista"
import { Aviso, Botao, Campo, estiloCampo, mensagem } from "../outros/pecas"

type Aba = "biblioteca" | "posicionamentos"

export function CatalogosDoDiagrama() {
  const [aba, setAba] = useState<Aba>("biblioteca")

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa
        titulo="Catálogos do diagrama"
        detalhe={aba === "biblioteca"
          ? "Os nomes de componente que o desenho usa"
          : "Onde cada componente entra, por sistema"}
      />

      <AvisoDeDadoGlobal oQueMuda={aba === "biblioteca"
        ? "a biblioteca de nomes que o desenho oferece em todos os projetos"
        : "o posicionamento padrão daquele sistema em todos os projetos"} />

      <div style={{ display: "flex", gap: 4, padding: "10px 18px 0", borderBottom: "1px solid var(--line2)" }}>
        {([["biblioteca", "Biblioteca de componentes"], ["posicionamentos", "Posicionamentos padrão"]] as [Aba, string][])
          .map(([chave, rotulo]) => (
            <button
              key={chave}
              onClick={() => setAba(chave)}
              style={{
                border: 0, borderBottom: "2px solid " + (aba === chave ? "var(--acao)" : "transparent"),
                background: "transparent", cursor: "pointer", padding: "6px 12px", fontSize: 12.5,
                color: aba === chave ? "var(--txt)" : "var(--txt2)",
                fontWeight: aba === chave ? 600 : 400,
              }}
            >
              {rotulo}
            </button>
          ))}
      </div>

      {aba === "biblioteca" ? <Biblioteca /> : <Posicionamentos />}
    </div>
  )
}

// ------------------------------------------------------------------ biblioteca

function Biblioteca() {
  const [busca, setBusca] = useState("")
  const [editando, setEditando] = useState<ComponenteDaBiblioteca | null>(null)
  const [novo, setNovo] = useState("")
  const [recado, setRecado] = useState<string | null>(null)
  const [confirmando, setConfirmando] = useState(0)
  const [ordem, setOrdem] = usarOrdemDaLista("biblioteca-componentes")
  const recente = usarRecemIncluido()
  const rolagem = useRef<HTMLDivElement>(null)

  const lista = usarBiblioteca(busca, ordem)
  const salvar = usarSalvarBiblioteca()
  const excluir = usarExcluirDaBiblioteca()

  const texto = editando ? editando.componente : novo
  const conferencia = usarConferirBiblioteca(editando ? "" : novo)

  const itens = lista.data ?? []
  const comEspaco = useMemo(
    () => itens.filter((i) => i.componente !== i.componente.trim()).length, [itens])
  usarRolarAteRecente(rolagem, recente.id, itens)

  function gravar() {
    setRecado(null)
    const valor = texto
    if (!valor.trim()) { setRecado("O nome não pode ser vazio."); return }

    salvar.mutate(
      { id: editando?.id, componente: valor },
      {
        onSuccess: (gravado) => {
          if (editando) {
            setRecado(`Componente ${editando.id} alterado.`)
          } else {
            setRecado(`Componente “${gravado?.componente ?? valor}” incluído`
              + `${gravado?.id ? ` — id ${gravado.id}` : ""}.`)
            // Uma busca digitada antes poderia esconder justamente o que acabou de entrar.
            setBusca("")
            recente.marcar(gravado?.id)
          }
          setEditando(null); setNovo("")
        },
        onError: (e) => setRecado(mensagem(e)),
      })
  }

  return (
    <>
      <div style={{
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end",
        padding: "12px 18px", borderBottom: "1px solid var(--line2)",
      }}>
        <Campo rotulo="Procurar" ajuda="A listagem sem busca corta em 500.">
          <input value={busca} onChange={(e) => setBusca(e.target.value)}
                 placeholder="parte do nome…" style={{ ...estiloCampo, width: 260 }} />
        </Campo>

        <Campo rotulo="Ordem">
          <SeletorDeOrdem valor={ordem} aoMudar={setOrdem} />
        </Campo>

        <Campo
          rotulo={editando ? `Alterar o ${editando.id}` : "Incluir"}
          ajuda={editando
            ? "O texto vai como está — inclusive os espaços."
            : conferencia.data?.existe
              ? "Já existe um componente com essa descrição."
              : "A API confere duplicata pela descrição."}
        >
          <input
            value={texto}
            onChange={(e) => editando
              ? setEditando({ ...editando, componente: e.target.value })
              : setNovo(e.target.value)}
            placeholder="nome do componente"
            style={{
              ...estiloCampo, width: 320,
              borderColor: !editando && conferencia.data?.existe ? "var(--erro)" : undefined,
            }}
          />
        </Campo>

        <Botao destaque aoClicar={gravar} ocupado={salvar.isPending}
               desligado={!texto.trim() || (!editando && conferencia.data?.existe === true)}>
          {editando ? "Gravar" : "Incluir"}
        </Botao>
        {editando && <Botao aoClicar={() => setEditando(null)}>Cancelar</Botao>}
      </div>

      {recado && <div style={{ padding: "8px 18px 0" }}><Aviso>{recado}</Aviso></div>}

      {comEspaco > 0 && (
        <div style={{ padding: "8px 18px 0" }}>
          <Aviso>
            <b>{comEspaco} de {itens.length} nomes têm espaço sobrando</b> na frente ou atrás —
            vieram de colagem. Aparecem aqui com <code>·</code> no lugar do espaço, porque é assim
            que estão gravados e é assim que o desenho os usa.
          </Aviso>
        </div>
      )}

      <div ref={rolagem} style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 14 }}>
        {lista.isPending ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Carregando…</p>
        ) : lista.error ? (
          <p style={{ fontSize: 12.5, color: "var(--erro)" }}>{mensagem(lista.error)}</p>
        ) : itens.length === 0 ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Nada com esse texto.</p>
        ) : (
          <div style={{ border: "1px solid var(--line2)", borderRadius: 6, overflow: "hidden" }}>
            {itens.map((i) => (
              <div key={i.id} data-recem-incluido={i.id === recente.id ? "" : undefined} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "5px 10px",
                borderBottom: "1px solid var(--line2)", fontSize: 11.5,
              }}>
                <span className="mono" style={{ color: "var(--txt3)", width: 62 }}>{i.id}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  {i.componente.trim() === ""
                    ? <i style={{ color: "var(--erro)" }}>(em branco)</i>
                    : <ComEspacoVisivel texto={i.componente} />}
                </span>
                <button onClick={() => { setEditando(i); setRecado(null) }} style={acao}>alterar</button>
                <button onClick={() => setConfirmando(i.id)} style={acao}>excluir</button>
              </div>
            ))}
          </div>
        )}

        {confirmando > 0 && (
          <div style={{
            marginTop: 10, padding: 10, borderRadius: 6, border: "1px solid var(--erro)",
          }}>
            <p style={{ margin: "0 0 8px", fontSize: 11.5, lineHeight: 1.5 }}>
              Excluir o componente <b>{confirmando}</b> da biblioteca. Ele some da lista que o
              desenho oferece — os diagramas que já o usam não mudam.
            </p>
            <div style={{ display: "flex", gap: 6 }}>
              <Botao
                aoClicar={() => excluir.mutate(confirmando, {
                  onSuccess: () => { setRecado(`Componente ${confirmando} excluído.`); setConfirmando(0) },
                  onError: (e) => { setRecado(mensagem(e)); setConfirmando(0) },
                })}
                ocupado={excluir.isPending}
              >
                Confirmo
              </Botao>
              <Botao aoClicar={() => setConfirmando(0)}>Cancelar</Botao>
            </div>
          </div>
        )}
      </div>

      <BarraDeEstado direita={`${itens.length} na lista`}>
        {busca
          ? `Filtrando por "${busca}".`
          : ordem === "inclusao"
            ? "Sem busca, a API devolve os 500 mais recentes."
            : "Sem busca, a API devolve os 500 primeiros em ordem alfabética."}
      </BarraDeEstado>
    </>
  )
}

/** Mostra o espaço de borda como `·`. O texto do meio fica como está. */
function ComEspacoVisivel({ texto }: { texto: string }) {
  const antes = texto.length - texto.trimStart().length
  const depois = texto.length - texto.trimEnd().length
  const meio = texto.trim()

  return (
    <>
      {antes > 0 && <span style={{ color: "var(--erro)" }} title={`${antes} espaço(s)`}>{"·".repeat(Math.min(antes, 8))}</span>}
      {meio}
      {depois > 0 && <span style={{ color: "var(--erro)" }} title={`${depois} espaço(s)`}>{"·".repeat(Math.min(depois, 8))}</span>}
    </>
  )
}

// -------------------------------------------------------------- posicionamentos

const VAZIO = { sistemaId: 0, apelido: "", cor: "", posicao: "", ordem: 0 }

function Posicionamentos() {
  const sistemas = usarSistemasDeConteudo()
  const [sistemaId, setSistemaId] = useState(0)
  const [form, setForm] = useState<typeof VAZIO & { id?: number }>(VAZIO)
  const [recado, setRecado] = useState<string | null>(null)

  const lista = usarPosicionamentos(sistemaId)
  const salvar = usarSalvarPosicionamento()
  const excluir = usarExcluirPosicionamento()
  const [ordem, setOrdem] = usarOrdemDaLista("posicionamentos-padrao")
  const recente = usarRecemIncluido()
  const rolagem = useRef<HTMLDivElement>(null)

  // A lista é pequena (é por sistema) e vem inteira: ordenar aqui é o mesmo que no banco.
  const itens = useMemo(
    () => ordenarLista(lista.data ?? [], ordem, (p) => p.id, (p) => p.apelido ?? ""),
    [lista.data, ordem])
  usarRolarAteRecente(rolagem, recente.id, itens)

  function gravar() {
    setRecado(null)
    if (!form.apelido.trim()) { setRecado("O apelido é o que identifica o componente no desenho."); return }

    salvar.mutate(
      { id: form.id, dados: { ...form, sistemaId } },
      {
        onSuccess: (gravado) => {
          if (form.id) {
            setRecado("Alterado.")
          } else {
            setRecado(`Posicionamento “${gravado?.apelido ?? form.apelido}” incluído`
              + `${gravado?.id ? ` — id ${gravado.id}` : ""}.`)
            recente.marcar(gravado?.id)
          }
          setForm(VAZIO)
        },
        onError: (e) => setRecado(mensagem(e)),
      })
  }

  return (
    <>
      <div style={{
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end",
        padding: "12px 18px", borderBottom: "1px solid var(--line2)",
      }}>
        <Campo rotulo="Sistema" ajuda="A lista é por sistema — sem ele não há o que mostrar.">
          <select value={sistemaId} onChange={(e) => {
                    setSistemaId(Number(e.target.value)); setForm(VAZIO); recente.marcar(null)
                  }}
                  style={{ ...estiloCampo, width: 280 }}>
            <option value={0}>escolha…</option>
            {sistemas.itens.map((s) => <option key={s.id} value={s.id}>{s.rotulo}</option>)}
          </select>
        </Campo>

        {sistemaId > 0 && (
          <>
            <Campo rotulo="Apelido"><input value={form.apelido}
              onChange={(e) => setForm({ ...form, apelido: e.target.value })}
              style={{ ...estiloCampo, width: 180 }} /></Campo>
            <Campo rotulo="Cor"><input value={form.cor}
              onChange={(e) => setForm({ ...form, cor: e.target.value })}
              style={{ ...estiloCampo, width: 110 }} placeholder="#C7E4FB" /></Campo>
            <Campo rotulo="Posição"><input value={form.posicao}
              onChange={(e) => setForm({ ...form, posicao: e.target.value })}
              style={{ ...estiloCampo, width: 110 }} /></Campo>
            <Campo rotulo="Ordem"><input type="number" value={form.ordem}
              onChange={(e) => setForm({ ...form, ordem: Number(e.target.value) })}
              style={{ ...estiloCampo, width: 80 }} /></Campo>

            <Botao destaque aoClicar={gravar} ocupado={salvar.isPending}>
              {form.id ? "Gravar" : "Incluir"}
            </Botao>
            {form.id && <Botao aoClicar={() => setForm(VAZIO)}>Cancelar</Botao>}

            <Campo rotulo="Ordem da lista">
              <SeletorDeOrdem valor={ordem} aoMudar={setOrdem} />
            </Campo>
          </>
        )}
      </div>

      {recado && <div style={{ padding: "8px 18px 0" }}><Aviso>{recado}</Aviso></div>}

      <div ref={rolagem} style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 14 }}>
        {sistemaId === 0 ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)", maxWidth: "62ch", lineHeight: 1.6 }}>
            Escolha um sistema. <b>A tabela é quase vazia em produção</b> — dos 14 sistemas que
            sondei, só um tinha registro, e eram dois. Vazio aqui é o normal, não defeito.
          </p>
        ) : lista.isPending ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Carregando…</p>
        ) : lista.error ? (
          <p style={{ fontSize: 12.5, color: "var(--erro)" }}>{mensagem(lista.error)}</p>
        ) : itens.length === 0 ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>
            Nenhum posicionamento para este sistema — o que é o caso da quase totalidade deles.
          </p>
        ) : (
          <div style={{ border: "1px solid var(--line2)", borderRadius: 6, overflow: "hidden" }}>
            {itens.map((p) => <LinhaDePosicionamento key={p.id} p={p} recente={p.id === recente.id}
              aoEditar={() => setForm({ ...p })}
              aoExcluir={() => excluir.mutate(p.id, {
                onSuccess: () => setRecado(`Posicionamento ${p.id} excluído.`),
                onError: (e) => setRecado(mensagem(e)),
              })} />)}
          </div>
        )}
      </div>

      <BarraDeEstado direita={sistemaId > 0 ? `${itens.length} no sistema ${sistemaId}` : undefined}>
        Apelido, cor, posição e ordem do componente no desenho daquele sistema.
      </BarraDeEstado>
    </>
  )
}

function LinhaDePosicionamento({ p, recente, aoEditar, aoExcluir }: {
  p: PosicionamentoPadrao
  recente: boolean
  aoEditar: () => void
  aoExcluir: () => void
}) {
  return (
    <div data-recem-incluido={recente ? "" : undefined} style={{
      display: "flex", alignItems: "center", gap: 10, padding: "5px 10px",
      borderBottom: "1px solid var(--line2)", fontSize: 11.5,
    }}>
      <span className="mono" style={{ color: "var(--txt3)", width: 54 }}>{p.id}</span>
      <b style={{ width: 180 }}>{p.apelido || "—"}</b>
      <span style={{ display: "flex", alignItems: "center", gap: 5, width: 110 }}>
        {p.cor && (
          <span style={{
            width: 11, height: 11, borderRadius: 2, background: p.cor,
            border: "1px solid var(--line)",
          }} />
        )}
        <span className="mono" style={{ color: "var(--txt2)" }}>{p.cor || "—"}</span>
      </span>
      <span style={{ width: 110, color: "var(--txt2)" }}>{p.posicao || "—"}</span>
      <span style={{ width: 60, color: "var(--txt3)" }}>ordem {p.ordem}</span>
      <span style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
        <button onClick={aoEditar} style={acao}>alterar</button>
        <button onClick={aoExcluir} style={acao}>excluir</button>
      </span>
    </div>
  )
}

const acao: React.CSSProperties = {
  border: 0, background: "transparent", padding: 0, cursor: "pointer",
  fontSize: 10.5, color: "var(--txt2)", textDecoration: "underline",
}
